import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

const ENDPOINT = '/api/admin/license-workbench/generate';
const JOB_ENDPOINT = '/api/admin/license-workbench/jobs';
const DOWNLOAD_ENDPOINT = '/api/admin/license-workbench/download';
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_MS = 15 * 60 * 1000;

const toLocalDateTimeValue = (date) => {
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const nowLocalDateTime = () => toLocalDateTimeValue(new Date());

const oneYearLaterLocalDateTime = () => {
  const value = new Date();
  value.setFullYear(value.getFullYear() + 1);
  return toLocalDateTimeValue(value);
};

const toUtcIso = (localDateTime) => {
  if (!localDateTime || typeof localDateTime !== 'string') {
    return '';
  }
  const parsed = new Date(localDateTime);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toISOString().replace(/\.\d{3}Z$/, 'Z');
};

const parseFileNameFromDisposition = (headerValue) => {
  if (!headerValue || typeof headerValue !== 'string') {
    return '';
  }
  const utfMatch = headerValue.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1]);
    } catch (_error) {
      return utfMatch[1];
    }
  }
  const match = headerValue.match(/filename="?([^";]+)"?/i);
  return match?.[1] || '';
};

export default function AdminLicenseWorkbenchPage() {
  const { apiClient, user } = useAuth();
  const location = useLocation();

  const isAdmin = useMemo(
    () => Array.isArray(user?.roles) && user.roles.includes('ADMIN'),
    [user],
  );

  const accessToken = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return (params.get('k') || '').trim();
  }, [location.search]);

  const [form, setForm] = useState({
    licenseId: '',
    customer: '',
    issuedAt: nowLocalDateTime(),
    expiresAt: oneYearLaterLocalDateTime(),
    maxUsers: 100,
    sourceBranch: '',
  });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  const canSubmit = isAdmin && accessToken.length > 0 && !busy;

  const onChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const hintAdornment = (hint) => (
    <InputAdornment position="end">
      <Tooltip title={hint} arrow>
        <IconButton size="small" tabIndex={-1} edge="end" aria-label="Подсказка">
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </InputAdornment>
  );

  const submit = async (event) => {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }

    setBusy(true);
    setStatus(null);
    try {
      const payload = {
        licenseId: form.licenseId.trim(),
        customer: form.customer.trim(),
        issuedAt: toUtcIso(form.issuedAt),
        expiresAt: toUtcIso(form.expiresAt),
        maxUsers: Number(form.maxUsers),
        sourceBranch: form.sourceBranch.trim() || null,
      };

      if (!payload.issuedAt || !payload.expiresAt) {
        throw new Error('Проверьте даты выдачи и окончания лицензии');
      }
      if (!Number.isFinite(payload.maxUsers) || payload.maxUsers <= 0) {
        throw new Error('Укажите корректное максимальное количество пользователей');
      }

      const startResponse = await apiClient.post(ENDPOINT, payload, {
        withCredentials: true,
        headers: {
          'X-License-Workbench-Token': accessToken,
        },
      });

      const jobId = startResponse?.data?.jobId;
      if (!jobId) {
        throw new Error('Сервер не вернул jobId');
      }

      setStatus({ type: 'info', text: 'Генерация запущена. Ожидание готовности архива...' });

      const startedAt = Date.now();
      let job;
      while (Date.now() - startedAt < MAX_POLL_MS) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

        const jobResponse = await apiClient.get(`${JOB_ENDPOINT}/${jobId}`, {
          withCredentials: true,
          headers: {
            'X-License-Workbench-Token': accessToken,
          },
        });

        job = jobResponse?.data;
        if (job?.status === 'READY') {
          break;
        }
        if (job?.status === 'FAILED') {
          throw new Error(job?.error || 'Генерация архива завершилась с ошибкой');
        }
      }

      if (!job || job.status !== 'READY') {
        throw new Error('Истекло время ожидания генерации архива');
      }

      const response = await apiClient.get(`${DOWNLOAD_ENDPOINT}/${jobId}`, {
        withCredentials: true,
        headers: {
          'X-License-Workbench-Token': accessToken,
        },
        responseType: 'blob',
      });

      const contentDisposition = response.headers?.['content-disposition'];
      const fileName = parseFileNameFromDisposition(contentDisposition) || 'license-package.zip';

      const href = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = href;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(href);

      const usedBranch = response.headers?.['x-onprem-source-branch'] || job?.source || 'unknown';
      setStatus({ type: 'success', text: `Архив сформирован. Источник on-prem: ${usedBranch}` });
    } catch (error) {
      const message = error?.response?.data?.message
        || error?.response?.data?.error
        || error?.message
        || 'Не удалось сформировать архив';
      setStatus({ type: 'error', text: message });
    } finally {
      setBusy(false);
    }
  };

  if (!isAdmin) {
    return <Alert severity="error">Доступ только для ADMIN.</Alert>;
  }

  if (!accessToken) {
    return <Alert severity="error">Недействительная специальная ссылка (нет параметра k).</Alert>;
  }

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', py: 3 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Stack spacing={2} component="form" onSubmit={submit}>
          <Typography variant="h5">Генератор лицензии и On-Prem архива</Typography>
          <Typography variant="body2" color="text.secondary">
            Скрытая страница. Формирует ZIP с готовым `onprem/onprem-ready.zip`, где лицензия уже встроена.
          </Typography>

          {status && <Alert severity={status.type}>{status.text}</Alert>}

          <TextField
            required
            label="Идентификатор лицензии"
            value={form.licenseId}
            onChange={onChange('licenseId')}
            placeholder="LIC-ACME-2026"
            InputProps={{
              endAdornment: hintAdornment('Уникальный ID лицензии для заказчика. Используется в статусах и логах.'),
            }}
          />

          <TextField
            required
            label="Заказчик"
            value={form.customer}
            onChange={onChange('customer')}
            placeholder="ООО Ромашка"
            InputProps={{
              endAdornment: hintAdornment('Название компании/заказчика, для которого выпускается лицензия.'),
            }}
          />

          <TextField
            required
            label="Дата и время выдачи"
            type="datetime-local"
            value={form.issuedAt}
            onChange={onChange('issuedAt')}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: hintAdornment('Дата начала действия лицензии. Выбирается через календарь.'),
            }}
          />

          <TextField
            required
            label="Дата и время окончания"
            type="datetime-local"
            value={form.expiresAt}
            onChange={onChange('expiresAt')}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: hintAdornment('Дата окончания действия лицензии. После неё система будет заблокирована лицензией.'),
            }}
          />

          <TextField
            required
            label="Максимум пользователей"
            type="number"
            value={form.maxUsers}
            onChange={onChange('maxUsers')}
            inputProps={{ min: 1, step: 1 }}
            InputProps={{
              endAdornment: hintAdornment('Максимальное количество зарегистрированных пользователей в on-prem инсталляции.'),
            }}
          />

          <TextField
            label="Версия релиза (необязательно)"
            value={form.sourceBranch}
            onChange={onChange('sourceBranch')}
            placeholder="latest или 2026-05-31"
            helperText="Берётся из MinIO пути releases/{version}/...; если пусто, используется latest"
            InputProps={{
              endAdornment: hintAdornment('Версия on-prem сборки в MinIO. Если не указывать, будет взят releases/latest.'),
            }}
          />

          <Button type="submit" variant="contained" disabled={!canSubmit}>
            {busy ? 'Генерация...' : 'Сгенерировать архив'}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
